import { useLanguage } from '@/hooks/useLanguageHook'

export type TranslationKey = keyof typeof translations.en

const translations = {
  en: {
    // Common
    appTitle: 'Order Management System',
    search: 'Search',
    reset: 'Reset',
    clear: 'Clear',
    cancel: 'Cancel',
    save: 'Save',
    delete: 'Delete',
    edit: 'Edit',
    add: 'Add',
    create: 'Create',
    update: 'Update',
    loading: 'Loading',
    noData: 'No data',
    actions: 'Actions',

    // Order Page
    orderManagement: 'Order Management',
    orderManagementDesc: 'Search and manage all customer orders',
    downloadCSV: 'Download CSV',
    createRandomOrder: 'Create random order',
    searchFilters: 'Search',
    ordersList: 'Orders List',
    orderNumber: 'Order #',
    date: 'Date',
    customer: 'Customer',
    items: 'Items',
    total: 'Total',
    status: 'Status',
    paymentStatus: 'Payment Status',
    noOrdersFound: 'No orders found',
    noOrdersFoundDesc: 'Orders will appear here once customers place them.',
    noMatchesFound: 'No matches found',
    noMatchesFoundDesc: 'Try adjusting your search criteria.',
    loadingOrders: 'Loading orders...',
    deleteOrder: 'Delete Order',
    deleteOrderConfirm:
      'Are you sure you want to delete this order? This action cannot be undone.',

    // Pagination
    showing: 'Showing',
    to: 'to',
    of: 'of',
    entries: 'entries',

    // Home Page
    dashboard: 'Dashboard',
    welcomeBack: 'Welcome Back',
    quickActions: 'Quick Actions',

    // Products Page
    productManagement: 'Product Management',
    productManagementDesc: 'Manage your product inventory',
    products: 'Products',
    productName: 'Product Name',
    sku: 'SKU',
    price: 'Price',
    stock: 'Stock',
    category: 'Category',
    addProduct: 'Add Product',
    editProduct: 'Edit Product',
    deleteProduct: 'Delete Product',
    noProductsFound: 'No products found',
    noProductsFoundDesc: 'Add your first product to get started.',
    loadingProducts: 'Loading products...',

    // Customers Page
    customerManagement: 'Customer Management',
    customerManagementDesc: 'Manage your customer database',
    customers: 'Customers',
    firstName: 'First Name',
    lastName: 'Last Name',
    email: 'Email',
    phone: 'Phone',
    addCustomer: 'Add Customer',
    editCustomer: 'Edit Customer',
    deleteCustomer: 'Delete Customer',
    noCustomersFound: 'No customers found',
    noCustomersFoundDesc: 'Add your first customer to get started.',
    loadingCustomers: 'Loading customers...',

    // Staff Page
    staffManagement: 'Staff Management',
    staffManagementDesc: 'Manage staff members',
    staff: 'Staff',
    staffName: 'Staff Name',
    staffCode: 'Staff Code',
    department: 'Department',
    addStaff: 'Add Staff',
    editStaff: 'Edit Staff',
    deleteStaff: 'Delete Staff',
    noStaffFound: 'No staff found',
    noStaffFoundDesc: 'Add your first staff member to get started.',
    loadingStaff: 'Loading staff...',

    // Area Page
    areaManagement: 'Area Management',
    areaManagementDesc: 'Manage areas and regions',
    areas: 'Areas',
    areaName: 'Area Name',
    subAreas: 'Sub Areas',
    prefectures: 'Prefectures',
    addArea: 'Add Area',
    editArea: 'Edit Area',
    deleteArea: 'Delete Area',
    noAreasFound: 'No areas found',
    noAreasFoundDesc: 'Add your first area to get started.',
    loadingAreas: 'Loading areas...',

    // Category Page
    categoryManagement: 'Category Management',
    categoryManagementDesc: 'Manage categories and classifications',
    categories: 'Categories',
    categoryName: 'Category Name',
    categoryCode: 'Category Code',
    categoryType: 'Category Type',
    addCategory: 'Add Category',
    editCategory: 'Edit Category',
    deleteCategory: 'Delete Category',
    noCategoriesFound: 'No categories found',
    noCategoriesFoundDesc: 'Add your first category to get started.',
    loadingCategories: 'Loading categories...',

    // Sales Report Page
    salesReport: 'Sales Report',
    salesReportDesc: 'View and analyze daily sales performance by staff',
    salesData: 'Sales Data',
    period: 'Period',
    revenue: 'Revenue',
    quantity: 'Quantity',
    dailyPerformanceReport: 'Daily Performance Report',
    salesMetricsForDate: 'Sales metrics and performance indicators for',
    loadingSalesReports: 'Loading sales reports...',
    noSalesReportsFound: 'No sales reports found',
    noSalesReportsForDate: 'No sales reports found for this date.',
    allStaff: 'All Staff',
    selectStaff: 'Select Staff',
    previousDay: 'Previous Day',
    nextDay: 'Next Day',
    clickSearchToLoad: 'Click Search to Load Data',
    selectDateAndClickSearch:
      'Select date and staff, then click Search to view sales reports.',

    // Sales Report Table Headers
    attendance: 'Attendance',
    newCustomers: 'New',
    later: 'Later',
    repeat: 'Repeat',
    passReturn: 'Pass Rtn',
    complaint: 'Complaint',
    help: 'Help',
    pass: 'Pass',
    accountsReceivableShort: 'Accts Rec',
    inProgress: 'In Progress',

    // Order New Page
    newOrder: 'New Order',
    newOrderDesc: 'Create a new order',
    orderRegistration: 'Order Registration',
    orderEdit: 'Order Edit',
    register: 'Register',
    editingExistingOrder: 'Edit existing order information',
    registeringNewOrder: 'Register new order',
    fillFakeData: 'Fill Fake Data',
    loadingOrderData: 'Loading order data...',

    // Order New Page - Sections
    orderInformation: 'Order Information',
    customerInformation: 'Customer Information',
    addressInformation: 'Address Information',
    workDetails: 'Work Details',
    otherInformation: 'Other Information',
    accounting: 'Accounting',

    // Order New Page - Order Header Fields
    orderNumberField: 'Order Number',
    dateTime: 'Date & Time',
    orderStaff: 'Staff',
    orderSource: 'Order Source',
    orderMedia: 'Order Media',
    orderCategory: 'Category',
    couponNumber: 'Coupon Number',
    autoGenerated: 'Auto-generated',
    pleaseSelect: 'Please select',
    select: 'Select',

    // Order New Page - Customer Fields
    witness: 'Witness',
    requester: 'Requester',
    billingParty: 'Billing Party',
    furigana: 'Furigana',
    corporation: 'Corporation',
    tel2: 'TEL2',
    phoneDisplayNewEmail: 'Phone number display (new email)',
    billingPhoneDisplay: 'Billing phone number display',
    transferName: 'Transfer Name',
    repeatCustomer: 'Repeat Customer',

    // Order New Page - Address Fields
    siteAddress: 'Site Address',
    billingAddress: 'Billing Address',
    zipCode: 'Zip Code',
    prefecture: 'Prefecture',
    area: 'Area',
    buildingType: 'Building Type',
    address: 'Address',

    // Order New Page - Work Fields
    location: 'Location',
    specificSpot: 'Specific Spot',
    workContent: 'Work Content',
    specificSymptoms: 'Specific Symptoms',

    // Order New Page - Other Fields
    explanationAtReception: 'Explanation at Reception',
    fees: 'Fees',
    paymentMethod: 'Payment Method',
    discount: 'Discount',
    deferredPayment: 'Deferred Payment',
    freeEstimate: 'Free Estimate',
    remarks: 'Remarks',
    inquiryContent: 'Inquiry Content',

    // Order New Page - Accounting Fields
    cashAmount: 'Cash Amount',
    credit: 'Credit',
    accountsReceivable: 'Accounts Receivable',
    paymentDueDate: 'Payment Due Date',
    totalSales: 'Total Sales',
    receiptNumber: 'Receipt Number',

    // Order New Page - Payment Methods
    transfer: 'Transfer',
    card: 'Card',
    cash: 'Cash',

    // Order New Page - Validation Messages
    validationError: 'Validation Error',
    dateTimeRequired: 'Date & Time is required',
    staffRequired: 'Staff is required',
    orderMediaRequired: 'Order Media is required',
    requesterNamePhoneRequired: 'Requester name and phone are required',
    mustBeLoggedIn: 'You must be logged in to create an order',

    // Order New Page - Success Messages
    orderUpdatedSuccess: 'Order updated successfully! Order Number:',
    orderRegisteredSuccess: 'Order registered successfully! Order Number:',

    // Order New Page - Error Messages
    failedToUpdateOrder: 'Failed to update order. Please try again.',
    failedToRegisterOrder: 'Failed to register order. Please try again.',
    failedToLoadOrderData: 'Failed to load order data',

    // Under Development Pages
    comingSoon: 'Coming Soon',
    underDevelopment: 'This feature is currently under development.',
    workingHardMessage:
      "We're working hard to bring you an amazing experience!",

    // Inquiry Search Page
    inquirySearch: 'Inquiry Search',
    inquirySearchDesc: 'Search and manage customer inquiries',
    loadingInquiries: 'Loading inquiries...',
    noInquiriesFound: 'No inquiries found',
    noInquiriesFoundDesc: 'Inquiries will appear here once they are created.',
    receptionDate: 'Reception Date',
    receptionNumber: 'Reception Number',
    receptionStaff: 'Reception Staff',
    client: 'Client',
    customerName: 'Customer Name',
    inquiryCategory: 'Inquiry Category',
    complaintCategory: 'Complaint Category',
    content: 'Content',
    judgment: 'Judgment',
    customerNameKana: 'Customer Name (Kana)',
    view: 'View',

    // Data Import Page
    dataImport: 'Data Import',
    dataImportDesc: 'Batch data import during off-peak hours',

    // Sales Transmission Page
    salesTransmission: 'Sales Report Transmission',
    salesTransmissionDesc: 'Automated sales report distribution',

    // Report Output Page
    reportOutput: 'Report Output',
    reportOutputDesc: 'Generate reports in various formats',

    // Common table
    name: 'Name',
    code: 'Code',
    type: 'Type',
    description: 'Description',
    loginId: 'Login ID',
    tel: 'Phone',
    mail: 'Email',
    permission: 'Permission',
    rank: 'Rank',
    salesOffice: 'Sales Office',

    // Staff Management
    createRandomStaff: 'Create random staff',
    deleteStaffConfirm: 'Are you sure you want to delete this staff member?',

    // Staff Form Dialog
    addNewStaff: 'Add New Staff',
    editStaffTitle: 'Edit Staff',
    updateStaffInfo: 'Update staff information',
    createStaffInfo:
      'Enter staff information to create a new Firebase account and staff record',
    loginCredentials: 'Login Credentials',
    loginIdLabel: 'Login ID',
    loginIdCannotBeChanged: 'Login ID cannot be changed',
    password: 'Password',
    minimumSixCharacters: 'Minimum 6 characters',
    passwordWillBeUpdated:
      'Password will be updated in Firebase Authentication',
    personalContactInfo: 'Personal & Contact Information',
    staffNameLabel: 'Staff Name',
    staffNameKana: 'Staff Name (Kana)',
    phoneLabel: 'Phone',
    emailLabel: 'Email',
    ccLabel: 'CC',
    workEmailCC: 'Work Email CC',
    mediaSalesEmail: 'Media & Sales Email',
    rolesResponsibilities: 'Roles and Responsibilities',
    authority: 'Authority',
    rankLabel: 'Rank',
    branchOffice: 'Branch/Office',
    assignedArea: 'Assigned Area',
    bySales: 'By Sales',
    salesRep: 'Sales Rep',
    nightShiftStaff: 'Night Shift Staff',
    technician: 'Technician',
    areaManager: 'Area Manager',
    executive: 'Executive',
    admin: 'Admin',
    manager: 'Manager',
    user: 'User',
    viewer: 'Viewer',

    // Customer Management
    createRandomCustomer: 'Create random customer',

    // Product Management
    createRandomProduct: 'Create random product',

    // Area Management
    subAreaName: 'Sub-Area Name',
    allAreas: 'All Areas',
    filterByArea: 'Filter by Area',
    prefectureName: 'Prefecture Name',
    displayOrder: 'Display Order',
    addSubArea: 'Add Sub-Area',
    addPrefecture: 'Add Prefecture',
    editSubArea: 'Edit Sub-Area',
    editPrefecture: 'Edit Prefecture',
    noSubAreasFound: 'No sub-areas found',
    noPrefecturesFound: 'No prefectures found',
    matchingSearch: 'matching your search.',
    addFirstItemAbove: 'Add your first item above.',
    createArea: 'Create Area',
    createSubArea: 'Create Sub-Area',
    createPrefecture: 'Create Prefecture',
    creating: 'Creating',
    saving: 'Saving',
    saveChanges: 'Save Changes',
    deleting: 'Deleting',
    createNewAreaDesc: 'Create a new area region.',
    createNewSubAreaDesc: 'Create a new sub-area or city.',
    createNewPrefectureDesc: 'Create a new prefecture.',
    areaNamePlaceholder: 'e.g., Kanto',
    subAreaNamePlaceholder: 'e.g., Tokyo A',
    prefectureNamePlaceholder: 'e.g., Tokyo',
    displayOrderPlaceholder: 'e.g., 1',
    areaOptional: 'Area (Optional)',
    areaRequired: 'Area *',
    selectAreaOptional: 'Select Area (optional)',
    selectAreaRequired: 'Select Area *',
    updateAreaInfo: 'Update the area information below.',
    updateSubAreaInfo: 'Update the sub-area information below.',
    updatePrefectureInfo: 'Update the prefecture information below.',
    confirmDeletion: 'Confirm Deletion',
    deleteConfirmMessage:
      'Are you sure you want to delete this item? This action cannot be undone.',
    itemToDelete: 'Item to delete',

    // Category Management
    initializeDefaults: 'Initialize Defaults',
    debuggingOnly: 'Debugging only',
    createRandomCategory: 'Create random category',
    createRandomData: 'Create Random Data',
    addNew: 'Add New',
    selectCategoryType: 'Select category type',
    noCategoryTypeSelected: 'No category type selected',
    selectCategoryTypeToView:
      'Please select a category type to view categories',
    noCategoriesFoundForType: 'No categories found for',
    addFirstCategoryToStart: 'Add your first category to get started!',
    createNewCategoryFor: 'Create a new category for',
    selectType: 'Select type',
    enterCategoryName: 'Enter category name',
    enterDisplayOrder: 'Enter display order',
    updateCategoryInfo: 'Update the category information',
    deleteCategoryConfirm: 'Are you sure you want to delete',
    categoryTypeManagement: 'Category Type Management',
    categoryTypeManagementDesc:
      'Manage category types with name and display order',
    categoryManagementTab: 'Category Management',
    categoryManagementTabDesc:
      'Manage categories with name, type, and display order',
    filterByCategoryType: 'Filter by Category Type',
    filterByThisType: 'Filter by this type',
    noCategoryTypesFound: 'No category types found',
    noCategoryTypesFoundDesc: 'Add your first category type to get started!',
    loadingCategoryTypes: 'Loading category types...',
    addCategoryType: 'Add Category Type',
    editCategoryType: 'Edit Category Type',
    deleteCategoryType: 'Delete Category Type',
    createNewCategoryType: 'Create a new category type',
    updateCategoryTypeInfo: 'Update the category type information',
    categoryTypeCode: 'Code',
    categoryTypeName: 'Name',
    categoryTypeDisplayOrder: 'Display Order',
    categoryCount: 'Categories',
    codeCannotBeChanged: 'Code (Cannot be changed)',
    enterCategoryTypeCode: 'Enter category type code',
    enterCategoryTypeName: 'Enter category type name',
    deleteCategoryTypeConfirm: 'Are you sure you want to delete category type',
    categoryTypeTab: 'Category Type',
    categoryTab: 'Category',

    // Profile Page
    profile: 'Profile',
    profileDesc: 'View and manage your profile information',
    loadingProfile: 'Loading profile...',
    noProfileFound: 'No profile found. Please contact support.',
    editName: 'Edit Name',
    updateDisplayName: 'Update your display name',
    displayNameRequired: 'Display name is required',
    profileUpdatedSuccess: 'Profile updated successfully',
    failedToUpdateProfile: 'Failed to update profile',
    enterDisplayName: 'Enter your display name',

    // Permission Messages
    permissionDenied: 'Permission Denied',
    onlyAdminCanCreateStaff:
      'Only admin users can create staff accounts. Please contact an administrator.',

    // Logout
    logout: 'Logout',
    logoutConfirmMessage: 'Are you sure you want to logout?',
  },
  ja: {
    // Common
    appTitle: '受注管理システム',
    search: '検索',
    reset: 'リセット',
    clear: 'クリア',
    cancel: 'キャンセル',
    save: '保存',
    delete: '削除',
    edit: '編集',
    add: '追加',
    create: '作成',
    update: '更新',
    loading: '読み込み中',
    noData: 'データなし',
    actions: '操作',

    // Order Page
    orderManagement: '受注管理',
    orderManagementDesc: '全ての顧客注文を検索・管理',
    downloadCSV: 'CSVダウンロード',
    createRandomOrder: 'ランダム注文作成',
    searchFilters: '検索',
    ordersList: '注文一覧',
    orderNumber: '注文番号',
    date: '日付',
    customer: '顧客',
    items: '商品',
    total: '合計',
    status: 'ステータス',
    paymentStatus: '支払状況',
    noOrdersFound: '注文が見つかりません',
    noOrdersFoundDesc: '顧客が注文すると、ここに表示されます。',
    noMatchesFound: '一致する結果がありません',
    noMatchesFoundDesc: '検索条件を調整してください。',
    loadingOrders: '注文を読み込み中...',
    deleteOrder: '注文を削除',
    deleteOrderConfirm:
      'この注文を削除してもよろしいですか？この操作は元に戻せません。',

    // Pagination
    showing: '表示中',
    to: '〜',
    of: '/',
    entries: '件',

    // Home Page
    dashboard: 'ダッシュボード',
    welcomeBack: 'お帰りなさい',
    quickActions: 'クイックアクション',

    // Products Page
    productManagement: '商品管理',
    productManagementDesc: '商品在庫を管理',
    products: '商品',
    productName: '商品名',
    sku: 'SKU',
    price: '価格',
    stock: '在庫',
    category: 'カテゴリー',
    addProduct: '商品追加',
    editProduct: '商品編集',
    deleteProduct: '商品削除',
    noProductsFound: '商品が見つかりません',
    noProductsFoundDesc: '最初の商品を追加してください。',
    loadingProducts: '商品を読み込み中...',

    // Customers Page
    customerManagement: '顧客管理',
    customerManagementDesc: '顧客データベースを管理',
    customers: '顧客',
    firstName: '名',
    lastName: '姓',
    email: 'メール',
    phone: '電話番号',
    addCustomer: '顧客追加',
    editCustomer: '顧客編集',
    deleteCustomer: '顧客削除',
    noCustomersFound: '顧客が見つかりません',
    noCustomersFoundDesc: '最初の顧客を追加してください。',
    loadingCustomers: '顧客を読み込み中...',

    // Staff Page
    staffManagement: 'スタッフ管理',
    staffManagementDesc: 'スタッフメンバーを管理',
    staff: 'スタッフ',
    staffName: 'スタッフ名',
    staffCode: 'スタッフコード',
    department: '部署',
    addStaff: 'スタッフ追加',
    editStaff: 'スタッフ編集',
    deleteStaff: 'スタッフ削除',
    noStaffFound: 'スタッフが見つかりません',
    noStaffFoundDesc: '最初のスタッフを追加してください。',
    loadingStaff: 'スタッフを読み込み中...',

    // Area Page
    areaManagement: 'エリア管理',
    areaManagementDesc: 'エリアと地域を管理',
    areas: 'エリア',
    areaName: 'エリア名',
    subAreas: 'サブエリア',
    prefectures: '都道府県',
    addArea: 'エリア追加',
    editArea: 'エリア編集',
    deleteArea: 'エリア削除',
    noAreasFound: 'エリアが見つかりません',
    noAreasFoundDesc: '最初のエリアを追加してください。',
    loadingAreas: 'エリアを読み込み中...',

    // Category Page
    categoryManagement: '区分管理',
    categoryManagementDesc: 'カテゴリーと分類を管理',
    categories: '区分',
    categoryName: '区分名',
    categoryCode: '区分コード',
    categoryType: '区分タイプ',
    addCategory: '区分追加',
    editCategory: '区分編集',
    deleteCategory: '区分削除',
    noCategoriesFound: '区分が見つかりません',
    noCategoriesFoundDesc: '最初の区分を追加してください。',
    loadingCategories: '区分を読み込み中...',

    // Sales Report Page
    salesReport: '営業報告送信',
    salesReportDesc: 'スタッフ別の日次売上実績を表示・分析',
    salesData: '売上データ',
    period: '期間',
    revenue: '売上高',
    quantity: '数量',
    dailyPerformanceReport: '日次実績レポート',
    salesMetricsForDate: '売上指標とパフォーマンス指標',
    loadingSalesReports: '売上レポートを読み込み中...',
    noSalesReportsFound: '売上レポートが見つかりません',
    noSalesReportsForDate: 'この日付の売上レポートが見つかりません。',
    allStaff: '全スタッフ',
    selectStaff: 'スタッフを選択',
    previousDay: '前日',
    nextDay: '翌日',
    clickSearchToLoad: '検索をクリックしてデータを読み込む',
    selectDateAndClickSearch:
      '日付とスタッフを選択し、検索をクリックして売上レポートを表示します。',

    // Sales Report Table Headers
    attendance: '出勤',
    newCustomers: '新規',
    later: '後日',
    repeat: 'リピート',
    passReturn: 'パス復',
    complaint: 'クレーム',
    help: 'ヘルプ',
    pass: 'パス',
    accountsReceivableShort: '売掛金',
    inProgress: '作業中',

    // Order New Page
    newOrder: '新規注文',
    newOrderDesc: '新規注文を作成',
    orderRegistration: '受注登録',
    orderEdit: '受注編集',
    register: '登録',
    editingExistingOrder: '既存受注の情報を編集します',
    registeringNewOrder: '新規受注の登録を行います',
    fillFakeData: 'Fill Fake Data',
    loadingOrderData: 'Loading order data...',

    // Order New Page - Sections
    orderInformation: '受注情報',
    customerInformation: '顧客情報',
    addressInformation: '住所情報',
    workDetails: '作業内容',
    otherInformation: 'その他情報',
    accounting: '経理',

    // Order New Page - Order Header Fields
    orderNumberField: '受付番号',
    dateTime: '受付日時',
    orderStaff: '受付者',
    orderSource: '受注先',
    orderMedia: '受注媒体',
    orderCategory: '受付内容',
    couponNumber: 'クーポン番号',
    autoGenerated: 'Auto-generated',
    pleaseSelect: '選択してください',
    select: '選択',

    // Order New Page - Customer Fields
    witness: '立会者',
    requester: '依頼者',
    billingParty: '請求先',
    furigana: 'フリガナ',
    corporation: '法人(店舗名)',
    tel2: 'TEL2',
    phoneDisplayNewEmail: '電話番号表示(新規メール)',
    billingPhoneDisplay: '請求先電話番号表示',
    transferName: '振込名義',
    repeatCustomer: 'リピート対象',

    // Order New Page - Address Fields
    siteAddress: '現場住所',
    billingAddress: '請求先住所',
    zipCode: '〒',
    prefecture: '都道府県',
    area: 'エリア',
    buildingType: '建物形態',
    address: '住所',

    // Order New Page - Work Fields
    location: '場所',
    specificSpot: '箇所',
    workContent: '内容',
    specificSymptoms: '具体的症状',

    // Order New Page - Other Fields
    explanationAtReception: '受付時説明',
    fees: '料金',
    paymentMethod: '支払',
    discount: '割引',
    deferredPayment: '後払い',
    freeEstimate: '見積り無料',
    remarks: '備考',
    inquiryContent: '問合せ内容',

    // Order New Page - Accounting Fields
    cashAmount: '現金金額',
    credit: 'クレジット',
    accountsReceivable: '売掛金額',
    paymentDueDate: '入金予定日',
    totalSales: '売上合計',
    receiptNumber: '領収書No',

    // Order New Page - Payment Methods
    transfer: '振込',
    card: 'カード',
    cash: '現金',

    // Order New Page - Validation Messages
    validationError: 'Validation Error',
    dateTimeRequired: '受付日時 (Date & Time) is required',
    staffRequired: '受付者 (Staff) is required',
    orderMediaRequired: '受注媒体 (Order Media) is required',
    requesterNamePhoneRequired: 'Requester name and phone are required',
    mustBeLoggedIn: 'You must be logged in to create an order',

    // Order New Page - Success Messages
    orderUpdatedSuccess: 'Order updated successfully! Order Number:',
    orderRegisteredSuccess: 'Order registered successfully! Order Number:',

    // Order New Page - Error Messages
    failedToUpdateOrder: 'Failed to update order. Please try again.',
    failedToRegisterOrder: 'Failed to register order. Please try again.',
    failedToLoadOrderData: 'Failed to load order data',

    // Under Development Pages
    comingSoon: '近日公開',
    underDevelopment: 'この機能は現在開発中です。',
    workingHardMessage: '素晴らしい体験をお届けするために開発を進めています！',

    // Inquiry Search Page
    inquirySearch: '問合せ検索',
    inquirySearchDesc: '顧客問い合わせの検索・管理',
    loadingInquiries: '問合せを読み込み中...',
    noInquiriesFound: '問合せが見つかりません',
    noInquiriesFoundDesc: '問合せが作成されると、ここに表示されます。',
    receptionDate: '受付日',
    receptionNumber: '受付番号',
    receptionStaff: '受付者',
    client: '受注先',
    customerName: '顧客名',
    inquiryCategory: '問合せ区分',
    complaintCategory: 'クレーム区分',
    content: '内容',
    judgment: '判定',
    customerNameKana: '顧客名カナ',
    view: '表示',

    // Data Import Page
    dataImport: '夜間データインポート',
    dataImportDesc: '夜間バッチデータのインポート',

    // Sales Transmission Page
    salesTransmission: '営業報告書送信',
    salesTransmissionDesc: '営業報告書の自動配信',

    // Report Output Page
    reportOutput: '各種帳票出力',
    reportOutputDesc: '各種形式でのレポート生成',

    // Common table
    name: '名前',
    code: 'コード',
    type: 'タイプ',
    description: '説明',
    loginId: 'ログインID',
    tel: 'TEL',
    mail: 'Mail',
    permission: '権限',
    rank: 'ランク',
    salesOffice: '営業所属',

    // Staff Management
    createRandomStaff: 'ランダムスタッフ作成',
    deleteStaffConfirm: 'このスタッフを削除してもよろしいですか？',

    // Staff Form Dialog
    addNewStaff: '新規スタッフ追加',
    editStaffTitle: 'スタッフ編集',
    updateStaffInfo: 'スタッフ情報を更新',
    createStaffInfo:
      'スタッフ情報を入力して、新しいFirebaseアカウントとスタッフレコードを作成',
    loginCredentials: 'ログイン情報',
    loginIdLabel: 'ログインID',
    loginIdCannotBeChanged: 'ログインIDは変更できません',
    password: 'パスワード',
    minimumSixCharacters: '最低6文字',
    passwordWillBeUpdated: 'パスワードはFirebase認証で更新されます',
    personalContactInfo: '個人情報・連絡先',
    staffNameLabel: '担当者名',
    staffNameKana: '担当者名（カナ）',
    phoneLabel: 'TEL',
    emailLabel: 'Mail',
    ccLabel: 'CC',
    workEmailCC: '作業メールCC',
    mediaSalesEmail: '媒体・売上メール',
    rolesResponsibilities: '役割と権限',
    authority: '権限',
    rankLabel: 'ランク',
    branchOffice: '営業所属',
    assignedArea: '担当エリア',
    bySales: '営業別',
    salesRep: '営業担当',
    nightShiftStaff: '夜間担当',
    technician: '職人',
    areaManager: 'エリアマネージャ',
    executive: '役員',
    admin: '管理者',
    manager: 'マネージャー',
    user: 'ユーザー',
    viewer: '閲覧者',

    // Customer Management
    createRandomCustomer: 'ランダム顧客作成',

    // Product Management
    createRandomProduct: 'ランダム商品作成',

    // Area Management
    subAreaName: 'サブエリア名',
    allAreas: '全エリア',
    filterByArea: 'エリアで絞込',
    prefectureName: '都道府県名',
    displayOrder: '表示順',
    addSubArea: 'サブエリア追加',
    addPrefecture: '都道府県追加',
    editSubArea: 'サブエリア編集',
    editPrefecture: '都道府県編集',
    noSubAreasFound: 'サブエリアが見つかりません',
    noPrefecturesFound: '都道府県が見つかりません',
    matchingSearch: '検索条件に一致するものが見つかりません。',
    addFirstItemAbove: '最初の項目を追加してください。',
    createArea: 'エリア作成',
    createSubArea: 'サブエリア作成',
    createPrefecture: '都道府県作成',
    creating: '作成中',
    saving: '保存中',
    saveChanges: '変更を保存',
    deleting: '削除中',
    createNewAreaDesc: '新しいエリア地域を作成します。',
    createNewSubAreaDesc: '新しいサブエリアまたは都市を作成します。',
    createNewPrefectureDesc: '新しい都道府県を作成します。',
    areaNamePlaceholder: '例: 関東',
    subAreaNamePlaceholder: '例: 東京A',
    prefectureNamePlaceholder: '例: 東京都',
    displayOrderPlaceholder: '例: 1',
    areaOptional: 'エリア (任意)',
    areaRequired: 'エリア *',
    selectAreaOptional: 'エリアを選択 (任意)',
    selectAreaRequired: 'エリアを選択 *',
    updateAreaInfo: 'エリア情報を更新してください。',
    updateSubAreaInfo: 'サブエリア情報を更新してください。',
    updatePrefectureInfo: '都道府県情報を更新してください。',
    confirmDeletion: '削除の確認',
    deleteConfirmMessage:
      'この項目を削除してもよろしいですか？この操作は元に戻せません。',
    itemToDelete: '削除する項目',

    // Category Management
    initializeDefaults: 'デフォルト初期化',
    debuggingOnly: 'デバッグ専用',
    createRandomCategory: 'ランダム区分作成',
    createRandomData: 'ランダムデータ作成',
    addNew: '新規追加',
    selectCategoryType: '区分タイプを選択',
    noCategoryTypeSelected: '区分タイプが選択されていません',
    selectCategoryTypeToView: '区分を表示する区分タイプを選択してください',
    noCategoriesFoundForType: '区分が見つかりません',
    addFirstCategoryToStart: '最初の区分を追加してください！',
    createNewCategoryFor: '新しい区分を作成します',
    selectType: 'タイプを選択',
    enterCategoryName: '区分名を入力',
    enterDisplayOrder: '表示順を入力',
    updateCategoryInfo: '区分情報を更新してください',
    deleteCategoryConfirm: '削除してもよろしいですか',
    categoryTypeManagement: '区分種類管理',
    categoryTypeManagementDesc: '名前と表示順で区分種類を管理',
    categoryManagementTab: '区分値管理',
    categoryManagementTabDesc: '名前、タイプ、表示順で区分値を管理',
    filterByCategoryType: '区分種類でフィルター',
    filterByThisType: 'この種類でフィルター',
    noCategoryTypesFound: '区分種類が見つかりません',
    noCategoryTypesFoundDesc: '最初の区分種類を追加してください！',
    loadingCategoryTypes: '区分種類を読み込み中...',
    addCategoryType: '区分種類追加',
    editCategoryType: '区分種類編集',
    deleteCategoryType: '区分種類削除',
    createNewCategoryType: '新しい区分種類を作成',
    updateCategoryTypeInfo: '区分種類情報を更新してください',
    categoryTypeCode: 'コード',
    categoryTypeName: '名前',
    categoryTypeDisplayOrder: '表示順',
    categoryCount: '区分数',
    codeCannotBeChanged: 'コード（変更不可）',
    enterCategoryTypeCode: '区分種類コードを入力',
    enterCategoryTypeName: '区分種類名を入力',
    deleteCategoryTypeConfirm: 'この区分種類を削除してもよろしいですか',
    categoryTypeTab: '区分種類',
    categoryTab: '区分値',

    // Profile Page
    profile: 'プロフィール',
    profileDesc: 'プロフィール情報を表示・管理',
    loadingProfile: 'プロフィールを読み込み中...',
    noProfileFound:
      'プロフィールが見つかりません。サポートにお問い合わせください。',
    editName: '名前を編集',
    updateDisplayName: '表示名を更新',
    displayNameRequired: '表示名は必須です',
    profileUpdatedSuccess: 'プロフィールが正常に更新されました',
    failedToUpdateProfile: 'プロフィールの更新に失敗しました',
    enterDisplayName: '表示名を入力してください',

    // Permission Messages
    permissionDenied: '権限がありません',
    onlyAdminCanCreateStaff:
      'スタッフアカウントの作成は管理者のみ可能です。管理者にお問い合わせください。',

    // Logout
    logout: 'ログアウト',
    logoutConfirmMessage: 'ログアウトしてもよろしいですか？',
  },
}

export function useTranslation() {
  const { language } = useLanguage()

  const t = (key: TranslationKey): string => {
    return translations[language][key] || key
  }

  return { t, language }
}
